import {
  FormEvent,
  InputHTMLAttributes,
  PureComponent,
  ReactNode,
} from 'react';

import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { srOnlyStyles } from 'src/components/accessibility';
import messages from 'src/i18n/common';

import * as styles from './search-field.style';

type Props = {
  currentSearch: string | null | undefined;
  handleFilterChange: (e: FormEvent<HTMLInputElement>) => void;
  intl: IntlShape;
  placeholder?: string;
  clearFilter?: () => void;
  fieldName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  forwardedRef?: (
    el: InputHTMLAttributes<HTMLInputElement> | null | undefined
  ) => void;
  elemBeforeInput?: ReactNode;
  width?: string;
  isDisabled?: boolean;
  tooltipContent?: string;
};

class SearchField extends PureComponent<Props> {
  render() {
    const {
      currentSearch,
      handleFilterChange,
      placeholder,
      clearFilter,
      fieldName,
      intl,
      onFocus,
      onBlur,
      forwardedRef,
      elemBeforeInput,
      width,
      isDisabled,
      tooltipContent,
    } = this.props;

    const textField = (
      <Textfield
        autoComplete="off"
        name={fieldName}
        id={fieldName}
        value={currentSearch || ''}
        onChange={handleFilterChange}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={isDisabled}
        ref={forwardedRef}
        elemBeforeInput={elemBeforeInput}
        elemAfterInput={
          clearFilter &&
          (currentSearch ? (
            <styles.ClearFilterButton
              aria-label={intl.formatMessage(messages.clearFilter)}
              appearance="subtle"
              spacing="none"
              onClick={clearFilter}
              iconAfter={<CrossIcon label="" />}
            />
          ) : (
            <styles.SearchIconWrapper>
              <SearchIcon label="" color={token('color.text.subtlest')} />
            </styles.SearchIconWrapper>
          ))
        }
      />
    );

    return (
      <styles.SearchFieldWrapper role="search" width={width}>
        <label css={srOnlyStyles} htmlFor={fieldName}>
          {placeholder}
        </label>
        {tooltipContent ? (
          <Tooltip content={tooltipContent} position="top">
            {textField}
          </Tooltip>
        ) : (
          textField
        )}
      </styles.SearchFieldWrapper>
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default injectIntl(SearchField);
